// Reliable server messages -> game state, HUD and audio.

import type { AudioEngine } from "../audio";
import type { Hud } from "../hud";
import type { MatchEndView, MatchPhase as HudPhase } from "../hud/types";
import { TICK_RATE } from "../net/constants";
import { HitZone } from "../proto/gunrun/v1/common_pb";
import { NoticeKind, RoundEndReason } from "../proto/gunrun/v1/game_pb";
import type {
  Damaged,
  Hit,
  Killed,
  MatchEnded,
  ModeEvent,
  PhaseChanged,
  Rewards,
  ServerMessage,
  Spawned,
  Welcome,
} from "../proto/gunrun/v1/game_pb";
import type { Renderer } from "../render";
import { bindingLabel } from "../settings/keybinds";
import { settings } from "../settings/store";
import { toast } from "../state/toasts";
import { clamp } from "./angles";
import { damageDirection, hitCue } from "./audioCues";
import { hudPhase, relativeBearing, teamTag } from "./convert";
import type { FxDirector } from "./fx";
import { buildScoreboard, killfeedEntry, matchEndView, rewardsView, teamName, withRewards } from "./hudBridge";
import type { InputController } from "./input";
import { endScreenLobbyHint } from "./lobby";
import type { GlassController } from "./glass";
import type { LobbyModel } from "./lobby";
import type { LocalPlayer } from "./local";
import type { ObjectiveTracker } from "./objectives";
import type { RemotePlayers } from "./remote";
import type { RoomState } from "./room";
import { probe } from "../perf/probe";

export interface MessageDeps {
  hud: Hud;
  audio: AudioEngine;
  renderer: Renderer;
  room: RoomState;
  remotes: RemotePlayers;
  fx: FxDirector;
  local: LocalPlayer;
  input: InputController;
  objectives: ObjectiveTracker;
  lobby: LobbyModel;
  glass: GlassController;
  /** The lobby model changed (roster, host, rules, phase, pings): the session republishes it. */
  onLobbyChanged(): void;
  /** Estimated current server tick. */
  serverTick(nowMs: number): number;
  onWelcome(w: Welcome): void;
  onSpawned(s: Spawned): void;
  onKicked(reason: string): void;
}

const END_REASON: Record<number, string> = {
  [RoundEndReason.ELIMINATION]: "Elimination",
  [RoundEndReason.BOMB_EXPLODED]: "Bomb exploded",
  [RoundEndReason.BOMB_DEFUSED]: "Bomb defused",
  [RoundEndReason.TIME_OUT]: "Time ran out",
};

export class MessageRouter {
  /** The match-end overlay as last shown, and when; `null` while no end screen is up. */
  private endShown: { view: MatchEndView; atMs: number } | null = null;
  /** Phase of the last `PhaseChanged` (snapshots repeat the phase, so only a message says it changed). */
  private lastPhase: HudPhase = "warmup";

  constructor(private readonly d: MessageDeps) {}

  handle(msg: ServerMessage, nowMs: number): void {
    const m = msg.msg;
    switch (m.case) {
      case "welcome":
        this.d.onWelcome(m.value);
        break;
      case "playerJoined":
        this.d.remotes.upsert(m.value);
        this.d.lobby.upsert(m.value);
        this.d.onLobbyChanged();
        break;
      case "playerLeft":
        this.d.remotes.remove(m.value.id);
        this.d.lobby.remove(m.value.id);
        this.d.onLobbyChanged();
        break;
      case "playerUpdated":
        if (m.value.id === this.d.room.myId) this.d.room.setMyTeam(teamTag(m.value.team));
        else this.d.remotes.upsert(m.value);
        this.d.lobby.upsert(m.value);
        this.d.onLobbyChanged();
        break;
      case "spawned":
        if (m.value.id === this.d.room.myId) this.d.onSpawned(m.value);
        break;
      case "hit":
        this.hit(m.value);
        break;
      case "damaged":
        this.damaged(m.value);
        break;
      case "killed":
        this.killed(m.value);
        break;
      case "scoreboard": {
        const room = this.d.room;
        const rows = room.setScoreboard(m.value.entries);
        this.d.lobby.setPings(m.value.entries);
        this.d.onLobbyChanged();
        this.d.hud.setScoreboard(buildScoreboard(room.mode, room.roomName, rows, room.teamScores()));
        room.pushMatch(this.d.serverTick(nowMs), nowMs, true);
        break;
      }
      case "modeEvent":
        this.modeEvent(m.value, nowMs);
        break;
      case "phaseChanged":
        this.phase(m.value, nowMs);
        break;
      case "matchEnded":
        this.matchEnded(m.value);
        break;
      case "chat": {
        const from = this.d.room.lookup(m.value.from);
        this.d.hud.chatPush({
          name: from ? from.name : "Server",
          team: from ? from.team : "none",
          teamOnly: m.value.teamOnly,
          text: m.value.text,
        });
        if (m.value.from !== this.d.room.myId) this.d.audio.playUi("notify");
        break;
      }
      case "notice": {
        const kind = m.value.kind;
        toast(m.value.text, kind === NoticeKind.ERROR ? "error" : kind === NoticeKind.SUCCESS ? "success" : "info");
        break;
      }
      case "kicked":
        this.d.onKicked(m.value.reason);
        break;
      case "roomSettings":
        this.d.room.applySettings(m.value);
        this.d.objectives.setMode(this.d.room.mode);
        this.d.lobby.setRules(m.value);
        this.d.onLobbyChanged();
        break;
      case "hostChanged":
        this.d.lobby.setHost(m.value.id);
        this.d.onLobbyChanged();
        break;
      case "rewards":
        this.rewards(m.value);
        break;
      case "glassBroken":
        this.d.glass.onBroken(m.value);
        break;
      case "glassRestored":
        this.d.glass.onRestored();
        break;
      default:
        break;
    }
  }

  private hit(h: Hit): void {
    probe?.hitreg.hit(h.tick, h.target, h.zone, h.damage);
    const kind = h.killed ? "kill" : h.zone === HitZone.HEAD ? "head" : "body";
    this.d.hud.hitmarker(kind);
    const cue = hitCue({ killed: h.killed, head: h.zone === HitZone.HEAD }, settings.value.audio);
    if (cue) this.d.audio.playHit(cue.kind, cue.style);
    // Spark where the bullet landed: roughly head / torso / legs of the victim.
    const head = this.d.renderer.players.headPosition(h.target);
    if (head) {
      const drop = h.zone === HitZone.HEAD ? 0.2 : h.zone === HitZone.BODY ? 0.75 : 1.2;
      this.d.renderer.effects.hitSpark({ x: head.x, y: head.y - drop, z: head.z });
    }
  }

  private damaged(dm: Damaged): void {
    const pose = this.d.local.camera.pose;
    const from = dm.from;
    const bearing = from ? relativeBearing(from.x - pose.pos.x, from.z - pose.pos.z, this.d.input.yaw) : 0;
    this.d.hud.damage(bearing, dm.damage);
    this.d.audio.playDamageTaken(clamp(dm.damage / Math.max(1, dm.health + dm.damage), 0, 1), damageDirection(from, pose.pos));
    if (this.d.local.alive) this.d.local.health = dm.health;
  }

  private killed(k: Killed): void {
    const room = this.d.room;
    const entry = killfeedEntry(k, room.lookup, room.myId);
    this.d.hud.killfeed(entry);
    if (k.victim !== room.myId) return;
    this.d.local.kill();
    const respawns = room.mode !== "defusal" && room.rules !== null;
    this.d.hud.death({
      killer: k.killer === undefined || k.killer === k.victim ? null : entry.killer,
      weapon: entry.weapon,
      respawnInSec: respawns ? (room.rules?.respawnDelaySec ?? null) : null,
    });
  }

  private phase(p: PhaseChanged, nowMs: number): void {
    const room = this.d.room;
    const before = this.lastPhase;
    room.phase = hudPhase(p.phase);
    this.lastPhase = room.phase;
    this.d.lobby.setPhase(room.phase);
    this.d.onLobbyChanged();
    room.phaseEndTick = p.endTick;
    if (room.phase !== "ended") {
      this.endShown = null;
      this.d.hud.matchEnd(null);
    }
    room.pushMatch(this.d.serverTick(nowMs), nowMs, true);
    if (room.phase === "countdown") this.d.hud.banner("GET READY", "Match starting", "neutral");
    else if (room.phase === "live") {
      this.d.hud.banner("FIGHT", undefined, "good");
      this.d.audio.playStinger("roundStart");
    } else if (room.phase === "round_live" && before === "round_freeze") {
      // The freeze is over (the bomb being planted repeats this phase with a new end tick).
      this.d.hud.banner("GO", undefined, "good");
      this.d.audio.playUi("go");
    }
  }

  private modeEvent(e: ModeEvent, nowMs: number): void {
    const room = this.d.room;
    const hud = this.d.hud;
    const ev = e.event;
    switch (ev.case) {
      case "roundStarted": {
        room.round = ev.value.round;
        const attacking = teamTag(ev.value.attackers) === room.myTeam;
        hud.banner(`ROUND ${ev.value.round}`, attacking ? "Attack" : "Defend", "neutral");
        this.d.audio.playStinger("roundStart");
        break;
      }
      case "roundEnded": {
        const winner = teamTag(ev.value.winner);
        const won = winner === room.myTeam;
        hud.banner(won ? "ROUND WON" : "ROUND LOST", END_REASON[ev.value.reason] ?? "", won ? "good" : "bad");
        this.d.audio.playStinger(won ? "roundWin" : "roundLose");
        break;
      }
      case "hillMoved": {
        // Sent when a hill is announced: it takes over (or starts scoring) at `activeTick`.
        const secs = Math.max(0, Math.round((ev.value.activeTick - this.d.serverTick(nowMs)) / TICK_RATE));
        hud.banner("NEXT HILL", `Hill ${ev.value.index + 1} in ${secs} s`, "neutral");
        this.d.audio.playUi("hillMove");
        break;
      }
      case "hillCaptured": {
        const mine = teamTag(ev.value.team) === room.myTeam;
        hud.banner(mine ? "HILL CAPTURED" : "HILL LOST", `Hill ${ev.value.index + 1}`, mine ? "good" : "bad");
        this.d.audio.playUi(mine ? "hillCapture" : "hillLost");
        break;
      }
      case "bombPlanted": {
        const attackers = room.myTeam === "a";
        hud.banner("BOMB PLANTED", `Site ${ev.value.site === 0 ? "A" : "B"}`, attackers ? "good" : "bad");
        this.d.audio.playUi("plantDone");
        break;
      }
      case "bombDefused":
        hud.banner("BOMB DEFUSED", undefined, room.myTeam === "b" ? "good" : "bad");
        this.d.audio.playUi("defuseDone");
        break;
      case "bombExploded": {
        const pos = this.d.objectives.bombPos();
        if (pos) this.d.fx.bombExplosion(pos);
        break;
      }
      case "bombDropped":
        hud.banner("BOMB DROPPED", undefined, "neutral");
        this.d.audio.playUi("notify");
        break;
      case "bombPickedUp":
        if (ev.value.by === room.myId) {
          hud.banner("BOMB PICKED UP", "Plant it on a site", "good");
          this.d.audio.playUi("confirm");
        }
        break;
      case "halftime":
        hud.banner("HALFTIME", "Sides switch", "neutral");
        break;
      case "matchPoint": {
        const t = teamTag(ev.value.team);
        hud.banner("MATCH POINT", t === "none" ? undefined : teamName(room.mode, t), "neutral");
        break;
      }
      default:
        break;
    }
  }

  private matchEnded(m: MatchEnded): void {
    const room = this.d.room;
    const view = matchEndView(m, room.mode, room.lookup, room.myId, room.myTeam);
    const lobby = this.d.lobby.view();
    if (lobby) view.lobby = endScreenLobbyHint(lobby.isHost, bindingLabel(settings.value.keybinds.lobby[0] ?? ""));
    this.endShown = { view, atMs: performance.now() };
    this.d.hud.matchEnd(view);
    if (view.tone === "good") this.d.audio.playStinger("roundWin");
    else if (view.tone === "bad") this.d.audio.playStinger("roundLose");
  }

  /** The match was recorded: show what it earned in the end overlay, if it is still up. */
  private rewards(r: Rewards): void {
    const shown = this.endShown;
    if (!shown) return;
    const now = performance.now();
    const view = withRewards(shown.view, rewardsView(r), (now - shown.atMs) / 1000);
    this.endShown = { view, atMs: now };
    this.d.hud.matchEnd(view);
  }
}
